import { z } from "zod";
import { WORKSPACE_ROOT } from "../../../workspace-root.js";
import { globList, globOne } from "./globs.js";

interface GrepContextLine {
  line: number;
  text: string;
  isMatch: boolean;
}

interface GrepMatch {
  path: string;
  line: number;
  text: string;
  context?: GrepContextLine[];
}

interface GrepOptions {
  regex?: boolean;
  ignoreCase?: boolean;
  context?: number;
  limit?: number;
  offset?: number;
  include?: string;
  exclude?: string[];
}

export interface GrepWorkspaceLike {
  fs: {
    grep(pattern: string, path: string, options?: GrepOptions): Promise<GrepMatch[]>;
  };
}

export interface GrepToolOptions {
  workspace: GrepWorkspaceLike;
}

const DEFAULT_LIMIT = 200;
const MAX_LIMIT = 1000;

export const grepInputSchema = z.object({
  path: z.string().default(WORKSPACE_ROOT).describe("Absolute file or directory to search."),
  query: z.string().describe("Literal string or regular expression to search for."),
  include: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .describe(
      'Glob relative to path that limits searched files, for example "**/*.ts" or ["**/*.ts", "**/*.md"].',
    ),
  exclude: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .describe(
      'Glob patterns to leave out, for example ["node_modules/**", "**/.git/**"]. An excluded directory is skipped along with everything below it.',
    ),
  regex: z.boolean().optional().describe("Interpret query as a regular expression."),
  ignoreCase: z.boolean().optional().describe("Ignore letter case."),
  context: z.number().int().min(0).max(10).optional(),
  limit: z.number().int().min(1).max(MAX_LIMIT).optional(),
  offset: z.number().int().min(0).optional(),
});

export const grepDescription =
  "Search workspace text with a literal string or regular expression. Results include paths and line numbers and can include surrounding lines.";

export interface GrepInput {
  path?: string;
  query: string;
  include?: string | string[];
  exclude?: string | string[];
  regex?: boolean;
  ignoreCase?: boolean;
  context?: number;
  limit?: number;
  offset?: number;
}

export type GrepResult =
  | {
      path: string;
      query: string;
      count: number;
      matches: GrepMatch[];
      nextOffset?: number;
    }
  | { error: string };

/**
 * Page matches for one query.
 *
 * Matching is literal and case-sensitive unless the caller opts into
 * `regex` or `ignoreCase`, which keeps a model's plain-string query from
 * being reinterpreted as a pattern.
 */
export async function grepInWorkspace(
  workspace: GrepWorkspaceLike,
  { path, query, include, exclude, regex, ignoreCase, context, limit, offset }: GrepInput,
): Promise<GrepResult> {
  const target = path ?? WORKSPACE_ROOT;
  try {
    const pageSize = limit ?? DEFAULT_LIMIT;
    const pageOffset = offset ?? 0;
    const searchOptions = {
      regex: regex ?? false,
      ignoreCase: ignoreCase ?? false,
      context: context ?? 0,
    };
    const matches = await workspace.fs.grep(query, target, {
      ...searchOptions,
      include: globOne(include),
      exclude: globList(exclude),
      limit: pageSize + 1,
      offset: pageOffset,
    });
    const truncated = matches.length > pageSize;
    const page = truncated ? matches.slice(0, pageSize) : matches;
    const result: {
      path: string;
      query: string;
      count: number;
      matches: GrepMatch[];
      nextOffset?: number;
    } = { path: target, query, count: page.length, matches: page };
    if (truncated) result.nextOffset = pageOffset + pageSize;
    return result;
  } catch (error) {
    return { error: error instanceof Error ? error.message : String(error) };
  }
}
